import { useCallback } from 'react'

import { bridge } from '~/services/bridge'
import { collectArchiveFiles } from '~/services/bridge/archiveUtils'
import { shell } from '~/services/bridge/thinShellClient'
import { createCompressedTarArchive } from '~/utils/tarUtils'

export interface FolderPickResult {
  tarFile: File
  folderName: string
  originalFilename: string
}

function base64ToFile(base64: string, fileName: string): File {
  const binaryString = atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return new File([bytes], fileName, { type: 'application/gzip' })
}

function pickFolderFromBrowser(): Promise<FolderPickResult | null> {
  return new Promise(resolve => {
    const input = document.createElement('input')
    input.type = 'file'
    input.setAttribute('webkitdirectory', 'true')
    input.multiple = true
    input.style.display = 'none'
    document.body.appendChild(input)

    let resolved = false
    const cleanup = () => {
      if (document.body.contains(input)) {
        document.body.removeChild(input)
      }
    }

    input.addEventListener('change', async () => {
      if (resolved) return
      resolved = true
      cleanup()
      if (input.files && input.files.length > 0) {
        try {
          const folderName =
            input.files[0].webkitRelativePath?.split('/')[0] || 'Selected Directory'
          const { tarFile, originalFilename } = await createCompressedTarArchive(
            input.files,
            '',
            false
          )
          resolve({ tarFile, folderName, originalFilename })
        } catch {
          resolve(null)
        }
      } else {
        resolve(null)
      }
    })

    input.addEventListener('cancel', () => {
      if (resolved) return
      resolved = true
      cleanup()
      resolve(null)
    })

    // Fallback for browsers that don't fire 'cancel'.
    // Large directories can take 10+ seconds to enumerate before 'change' fires,
    // so we use a generous timeout to avoid prematurely resolving as cancelled.
    const handleFocus = () => {
      setTimeout(() => {
        if (!resolved) {
          resolved = true
          cleanup()
          resolve(null)
        }
      }, 60_000)
      window.removeEventListener('focus', handleFocus)
    }
    window.addEventListener('focus', handleFocus)

    input.click()
  })
}

async function pickFolderFromPlugin(title?: string): Promise<FolderPickResult | null> {
  const result = await bridge.pickFolder(title)
  if (!result.success || !result.data) return null

  const { path, name } = result.data
  const files = await collectArchiveFiles(path)
  if (files.length === 0) return null

  const archive = await shell.createArchive(path, files, { prefix: name })
  const tarFile = base64ToFile(archive.base64Content, `${name}.tar.gz`)
  return { tarFile, folderName: name, originalFilename: `${name}.tar.gz` }
}

/**
 * Unified folder picker that works in both browser and plugin environments.
 *
 * Browser: opens a native file dialog with `webkitdirectory` and tars the result.
 * Plugin: opens a native OS folder dialog via the bridge and tars via native archive.
 *
 * Both paths return the same `{ tarFile, folderName, originalFilename }`.
 */
export function useFolderPicker() {
  const pickFolder = useCallback(async (title?: string): Promise<FolderPickResult | null> => {
    if (bridge.isInPlugin()) {
      return pickFolderFromPlugin(title)
    }
    return pickFolderFromBrowser()
  }, [])

  return { pickFolder }
}
